import { useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';
import type { TFunction } from 'i18next';
import {
    CheckIcon,
    WarningCircleIcon,
    ArrowClockwiseIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { FileUploadItem, FileUploadStatus } from '@/types/source-creation';
import { SourceStatus } from '@/types/source';
import { formatFileSize } from '@/utils/storage';
import { CircularProgress, VideoThumbnail } from '@/components';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { Spinner } from '@/components/Spinner';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    getSourceStatusLabel,
    isSourceCardNavigationEnabled,
} from '@/features/SourceView/sourceStatusDisplay';
import { AppRoutes } from '@/constants/routes';

interface FileSourceStatusDrawerItemProps {
    item: FileUploadItem;
    onRetry: (itemId: string) => void;
}

function getSecondaryDescription(
    item: FileUploadItem,
    translate: TFunction,
): string {
    const fileSizeLabel = formatFileSize(item.fileSize);

    if (item.status === FileUploadStatus.UPLOADING) {
        return translate('status.uploading');
    }

    if (item.status !== FileUploadStatus.COMPLETED || !item.createdSourceId) {
        return fileSizeLabel;
    }

    if (item.sourceStatus === undefined) {
        return translate('card.generatingPreview');
    }

    if (item.sourceStatus === SourceStatus.COMPLETED) {
        return fileSizeLabel;
    }

    if (item.sourceStatus === SourceStatus.PROCESSING) {
        return `${getSourceStatusLabel(item.sourceStatus, translate)} ${item.sourceProgress ?? 0}%`;
    }

    return getSourceStatusLabel(item.sourceStatus, translate);
}

export function FileSourceStatusDrawerItem({
    item,
    onRetry,
}: FileSourceStatusDrawerItemProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

    useEffect(() => {
        const url = URL.createObjectURL(item.file);
        setThumbnailUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [item.file]);

    const handleRetry = () => {
        onRetry(item.id);
    };

    const secondaryDescription = getSecondaryDescription(item, t);

    const showUploadProgressRing = item.status === FileUploadStatus.UPLOADING;

    const isBackendPhase =
        item.status === FileUploadStatus.COMPLETED &&
        Boolean(item.createdSourceId) &&
        item.sourceStatus !== SourceStatus.COMPLETED &&
        item.sourceStatus !== SourceStatus.ERROR;

    const showAnalyzingProgressRing =
        isBackendPhase && item.sourceStatus === SourceStatus.PROCESSING;

    const showPreparingSpinner =
        isBackendPhase && item.sourceStatus !== SourceStatus.PROCESSING;

    const showUploadCompleteSuccessIcon =
        item.status === FileUploadStatus.COMPLETED &&
        (!item.createdSourceId || item.sourceStatus === SourceStatus.COMPLETED);

    const showBackendErrorIcon =
        item.status === FileUploadStatus.COMPLETED &&
        item.createdSourceId &&
        item.sourceStatus === SourceStatus.ERROR;

    const canOpenSourcePage =
        Boolean(item.createdSourceId) &&
        isSourceCardNavigationEnabled(item.sourceStatus);

    const sourcePageHref =
        item.createdSourceId &&
        generatePath(AppRoutes.SOURCE, {
            sourceId: item.createdSourceId,
        });

    return (
        <div className="flex items-center gap-3 p-3 border-b border-grey-700 last:border-b-0">
            <div className="flex-shrink-0 w-16 h-9 rounded overflow-hidden bg-grey-700">
                {thumbnailUrl && (
                    <VideoThumbnail
                        src={thumbnailUrl}
                        className="w-full h-full object-cover"
                    />
                )}
            </div>

            <div className="flex-1 min-w-0">
                {canOpenSourcePage && sourcePageHref ? (
                    <a
                        href={sourcePageHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-white truncate no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400 block min-w-0"
                    >
                        {item.fileName}
                    </a>
                ) : (
                    <p className="text-xs text-white truncate">
                        {item.fileName}
                    </p>
                )}
                <p className="text-xs text-grey-300">{secondaryDescription}</p>

                {item.status === FileUploadStatus.FAILED && item.error && (
                    <p className="text-xs text-red-500 mt-1">{item.error}</p>
                )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {showUploadProgressRing && (
                    <CircularProgress progress={item.progress} size={5} />
                )}

                {showAnalyzingProgressRing && (
                    <CircularProgress
                        progress={item.sourceProgress ?? 0}
                        size={5}
                    />
                )}

                {showPreparingSpinner && <Spinner variant="light" size={20} />}

                {showUploadCompleteSuccessIcon && (
                    <div className="flex size-5 items-center justify-center rounded-full bg-success-100">
                        <CheckIcon
                            weight="bold"
                            className="size-3 text-white"
                        />
                    </div>
                )}

                {showBackendErrorIcon && (
                    <WarningCircleIcon className="w-5 h-5 text-red-500" />
                )}

                {item.status === FileUploadStatus.FAILED && (
                    <>
                        <WarningCircleIcon className="w-5 h-5 text-red-500" />
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleRetry}
                                    className="text-grey-200 hover:text-white-100 transition-colors"
                                    aria-label={t('statusDrawer.retryUpload')}
                                >
                                    <ArrowClockwiseIcon className="w-5 h-5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t('statusDrawer.retryUpload')}
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    );
}
